import api from './api';
import { uploadAwsFile } from './uploadService';
import type { Branch, ServiceCategory, Service, BranchSettings, RoomResource, ShiftConfig, MessageTemplate } from '../types';

// ─── Branch ───────────────────────────────────────────────────────────────────
export const fetchBranch   = () =>
  api.get<{ success: boolean; data: Branch }>('/settings/branch');

export const updateBranch  = (data: Partial<Branch>) =>
  api.put<{ success: boolean; data: Branch }>('/settings/branch', data);

export const addHoliday    = (date: string, label: string) =>
  api.post<{ success: boolean; data: Branch }>('/settings/branch/holidays', { date, label });

export const deleteHoliday = (holidayId: string) =>
  api.delete<{ success: boolean; data: Branch }>(`/settings/branch/holidays/${holidayId}`);

export const uploadBranchLogo = (file: File) => {
  return uploadAwsFile(file, 'branch-logos').then(result => ({ data: { success: true, data: result.entity as Branch } }));
};

export const uploadHomeBanner = (file: File) =>
  uploadAwsFile(file, 'mobile-home-banners').then(result => ({
    data: { success: true, data: result.entity as BranchSettings },
  }));

export const deleteHomeBanner = (url: string) =>
  api.delete<{ success: boolean; data: BranchSettings }>('/settings/home-banners', { data: { url } });

export const reorderHomeBanners = (urls: string[]) =>
  api.put<{ success: boolean; data: BranchSettings }>('/settings/home-banners/order', { urls });

// ─── Service Categories ───────────────────────────────────────────────────────
export const fetchCategories = () =>
  api.get<{ success: boolean; data: ServiceCategory[] }>('/settings/service-categories');

export const createCategory  = (data: { name: string; displayName: string; parentCategory?: string | null; sortOrder?: number; genders?: ServiceCategory['genders'] }) =>
  api.post<{ success: boolean; data: ServiceCategory }>('/settings/service-categories', data);

export const updateCategory  = (id: string, data: Partial<ServiceCategory>) =>
  api.put<{ success: boolean; data: ServiceCategory }>(`/settings/service-categories/${id}`, data);

export const uploadCategoryImage = async (id: string, file: File) => {
  const uploaded = await uploadAwsFile(file, 'service-categories', { targetId: id });
  return { data: { success: true, data: uploaded.entity as ServiceCategory } };
};

export const deleteCategory  = (id: string) =>
  api.delete<{ success: boolean }>(`/settings/service-categories/${id}`);

// ─── Rooms / Chairs ───────────────────────────────────────────────────────────
export const fetchRooms = (status: 'active'|'inactive'|'all' = 'active') =>
  api.get<{ success: boolean; data: RoomResource[] }>('/settings/rooms', { params: { status } });

export const createRoom = (data: { name: string; type: 'Chair'|'Room' }) =>
  api.post<{ success: boolean; data: RoomResource }>('/settings/rooms', data);

export const updateRoom = (id: string, data: Partial<RoomResource>) =>
  api.put<{ success: boolean; data: RoomResource }>(`/settings/rooms/${id}`, data);

export const deleteRoom = (id: string) =>
  api.delete<{ success: boolean }>(`/settings/rooms/${id}`);

// ─── Shift Configuration ──────────────────────────────────────────────────────
export const fetchShifts = (status: 'active'|'all' = 'active') =>
  api.get<{ success: boolean; data: ShiftConfig[] }>('/settings/shifts', { params: { status } });

export const createShift = (data: Pick<ShiftConfig, 'name'|'startTime'|'endTime'>) =>
  api.post<{ success: boolean; data: ShiftConfig }>('/settings/shifts', data);

export const updateShift = (id: string, data: Partial<ShiftConfig>) =>
  api.put<{ success: boolean; data: ShiftConfig }>(`/settings/shifts/${id}`, data);

export const deleteShift = (id: string) =>
  api.delete<{ success: boolean; data: ShiftConfig }>(`/settings/shifts/${id}`);

// ─── Services ─────────────────────────────────────────────────────────────────
export interface ServiceFilters { search?: string; category?: string; gender?: string; tag?: string; status?: string; limit?: number; }

export const fetchServices = (params?: ServiceFilters) =>
  api.get<{ success: boolean; data: Service[]; total: number }>('/settings/services', { params });

export interface ServiceOverviewStats {
  totalBookings: number; completedBookings: number; completionRate: number;
  uniqueClients: number; totalRevenue: number; unitsSold: number;
  averageRating: number; ratingCount: number; monthBookings: number; monthRevenue: number;
}
export interface ServiceOverviewAppointment {
  _id: string; appointmentNumber: string; date: string; startTime: string; status: string;
  client?: { firstName: string; lastName: string; mobile: string };
  services: Array<{ service: string; stylist?: { firstName: string; lastName: string } }>;
}
export const fetchServiceOverview = (id: string) =>
  api.get<{ success: boolean; data: { service: Service; stats: ServiceOverviewStats; recentAppointments: ServiceOverviewAppointment[] } }>(`/settings/services/${id}/overview`);

export const createService = (data: Omit<Service, '_id' | 'branch' | 'images'>) =>
  api.post<{ success: boolean; data: Service }>('/settings/services', data);

export const updateService = (id: string, data: Partial<Service>) =>
  api.put<{ success: boolean; data: Service }>(`/settings/services/${id}`, data);

export const deleteService = (id: string) =>
  api.delete<{ success: boolean }>(`/settings/services/${id}`);

export const uploadServiceImage = (id: string, file: File, title: string) => {
  return uploadAwsFile(file, 'services', { targetId: id, title }).then(result => ({ data: { success: true, data: result.entity as Service } }));
};

export const setFeaturedServiceImage = (id: string, imageId: string) =>
  api.put<{ success: boolean; data: Service }>(`/settings/services/${id}/images/${imageId}/feature`);
export const setThumbnailServiceImage = (id:string,imageId:string)=>
  api.put<{success:boolean;data:Service}>(`/settings/services/${id}/images/${imageId}/thumbnail`);
export const setInfographicServiceImage = (id:string,imageId:string)=>
  api.put<{success:boolean;data:Service}>(`/settings/services/${id}/images/${imageId}/infographic`);

export const deleteServiceImage = (id: string, imageId: string) =>
  api.delete<{ success: boolean; data: Service }>(`/settings/services/${id}/images/${imageId}`);

export const exportServicesCsv = () =>
  api.get<Blob>('/settings/services/export', { responseType: 'blob' });

export interface ServiceImportResult { created: number; updated: number; skipped: number; errors: string[]; }
export const importServicesCsv = (file: File) => {
  const body = new FormData();
  body.append('file', file);
  return api.post<{ success: boolean; data: ServiceImportResult }>('/settings/services/import', body);
};

// ─── Settings ─────────────────────────────────────────────────────────────────
export const fetchSettings  = () =>
  api.get<{ success: boolean; data: BranchSettings }>('/settings');

export const updateSettings = (data: Partial<BranchSettings>) =>
  api.put<{ success: boolean; data: BranchSettings }>('/settings', data);

export type MessageTemplateInput = Omit<MessageTemplate, '_id' | 'variables' | 'createdAt' | 'updatedAt'>;
export const fetchMessageTemplates = (channel?: MessageTemplate['channel']) =>
  api.get<{ success:boolean; data:MessageTemplate[] }>('/settings/message-templates', { params:channel ? { channel } : undefined });
export const createMessageTemplate = (data:MessageTemplateInput) =>
  api.post<{ success:boolean; data:MessageTemplate }>('/settings/message-templates', data);
export const updateMessageTemplate = (id:string, data:MessageTemplateInput) =>
  api.put<{ success:boolean; data:MessageTemplate }>(`/settings/message-templates/${id}`, data);
export const deactivateMessageTemplate = (id:string) =>
  api.delete<{ success:boolean; data:MessageTemplate }>(`/settings/message-templates/${id}`);
export interface CustomerClassification{_id:string;name:string;isActive:boolean;sortOrder:number;}
export const fetchCustomerClassifications=()=>api.get<{success:boolean;data:CustomerClassification[]}>('/settings/customer-classifications');
export const createCustomerClassification=(name:string)=>api.post<{success:boolean;data:CustomerClassification}>('/settings/customer-classifications',{name});
export const updateCustomerClassification=(id:string,data:Partial<CustomerClassification>)=>api.put<{success:boolean;data:CustomerClassification}>(`/settings/customer-classifications/${id}`,data);
export const deleteCustomerClassification=(id:string)=>api.delete(`/settings/customer-classifications/${id}`);

export interface ExpenseCategorySetting {_id:string;name:string;description:string;isActive:boolean;sortOrder:number;}
export type ExpenseCategoryInput=Omit<ExpenseCategorySetting,'_id'>;
export const fetchExpenseCategories=()=>api.get<{success:boolean;data:ExpenseCategorySetting[]}>('/settings/expense-categories');
export const createExpenseCategory=(data:ExpenseCategoryInput)=>api.post<{success:boolean;data:ExpenseCategorySetting}>('/settings/expense-categories',data);
export const updateExpenseCategory=(id:string,data:ExpenseCategoryInput)=>api.put<{success:boolean;data:ExpenseCategorySetting}>(`/settings/expense-categories/${id}`,data);
export const deleteExpenseCategory=(id:string)=>api.delete<{success:boolean;data:{_id:string}}>(`/settings/expense-categories/${id}`);

export type FaqType = 'Common'|'Packages'|'Membership'|'Voucher'|'Service'|'Rewards';
export interface Faq { _id:string;question:string;answer:string;type:FaqType;isActive:boolean;sortOrder:number; }
export type FaqInput = Omit<Faq, '_id'>;
export const fetchFaqs=(type?:FaqType)=>api.get<{success:boolean;data:Faq[]}>('/settings/faqs',{params:type?{type}:undefined});
export const createFaq=(data:FaqInput)=>api.post<{success:boolean;data:Faq}>('/settings/faqs',data);
export const updateFaq=(id:string,data:FaqInput)=>api.put<{success:boolean;data:Faq}>(`/settings/faqs/${id}`,data);
export const deleteFaq=(id:string)=>api.delete<{success:boolean;data:{_id:string}}>(`/settings/faqs/${id}`);

export interface BusinessTarget { _id?: string; month: string; revenue: number; services: number; newClients: number; }
export const fetchBusinessTarget = (month: string) =>
  api.get<{ success: boolean; data: BusinessTarget }>('/settings/targets', { params: { month } });
export const saveBusinessTarget = (data: BusinessTarget) =>
  api.put<{ success: boolean; data: BusinessTarget }>('/settings/targets', data);
export const exportBranchData = () => api.get<Blob>('/settings/data/export', { responseType: 'blob' });
export const importBranchData = (file: File) => {
  const body = new FormData(); body.append('file', file);
  return api.post<{ success: boolean; data: Record<string, number> }>('/settings/data/import', body);
};
